<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import WMOverlay from "./WMOverlay.vue";
import Icon from "./Icon.vue";
import FlashContainer from "mu-vue-utils/Components/FlashContainer.vue";
import Loader from "./Loader.vue";
import LoginForm from "./auth/LoginForm.vue";
import ChairGate from "./auth/ChairGate.vue";
import ChairSwitcherDialog from "./auth/ChairSwitcherDialog.vue";
import HelpPanel from "./help/HelpPanel.vue";
import { useI18n } from "../composables/I18n";
import { useAuth } from "../composables/useAuth";
import { useWM } from "../composables/WM";
import { chairTL } from "../generated/tl";
import { chairPositionLabel } from "../services/chairLabel";
import type { ChairListItemDto } from "../types/chair";

const { t } = useI18n();
const wm = useWM();
const { currentUser, currentChairId, resolved, resolve, logout } = useAuth();

onMounted(resolve);

// Just for the "{{ jméno }} — {{ aktuální pozice }}" label next to the username — ChairGate.vue/
// ChairSwitcherDialog.vue each fetch their own copy independently when they actually need to show
// the full pickable list.
const myChairs = ref<ChairListItemDto[]>([]);
watch(currentChairId, async (id) => {
  if (id === null) return;
  const all = await chairTL.findChairs();
  myChairs.value = all.filter(c => c.user.id === currentUser.value?.id);
}, { immediate: true });

const currentChairLabel = computed(() => {
  const item = myChairs.value.find(c => c.chair.id === currentChairId.value);
  return item ? chairPositionLabel(item) : null;
});

const openChairSwitcher = () => wm.openModal(ChairSwitcherDialog, {}, 'Vybrat pozici', true);
// onClose (docs/tasks/049.md): help sama udržuje #help:... v URL při procházení stránek
// (HelpPanel.vue's selectFile), ale nikdo dosud hash needmazal při zavření — po zavření a reloadu by
// se help kvůli deep-link watcheru níže hned znovu otevřela. replaceState, ne pushState, ať zavření
// nepřidává záznam do historie prohlížeče.
const openHelp = (file?: string) => wm.openSideModal(HelpPanel, { initialFile: file }, 'Nápověda', true, () => {
  history.replaceState(null, '', window.location.pathname + window.location.search);
  return true;
});

// Odkaz na nápovědu lze kdekoliv v aplikaci (popisky, nápovědné texty apod.) napsat jako
// <a href="#help:cesta/k/souboru.md">, aniž by autor odkazu musel volat WM sám — zachytíme klik na
// document úrovni (odkaz může být kdekoliv v DOM, i uvnitř jiného modálu) a otevřeme HelpPanel rovnou
// na dané stránce.
const onDocumentClick = (event: MouseEvent) => {
  const anchor = (event.target as HTMLElement).closest?.('a');
  const href = anchor?.getAttribute('href');
  if (!href?.startsWith('#help:')) return;
  event.preventDefault();
  openHelp(href.slice('#help:'.length));
};
onMounted(() => document.addEventListener('click', onDocumentClick));
onUnmounted(() => document.removeEventListener('click', onDocumentClick));

// Deep-link: appka může naběhnout s URL obsahující #help:cesta/k/souboru.md (stejné schéma jako
// onDocumentClick výše — HelpPanel taky sám udržuje URL aktuální, viz history.replaceState v
// selectFile) — otevřeme help rovnou na dané stránce. Čeká se na vybranou pozici (chair), protože
// dřív TL volání v HelpPanel spadnou na neautorizovaný přístup, a spustí se jen jednou, aby se help
// znovu neotvíral při každém přepnutí pozice.
let helpOpenedFromHash = false;
watch(currentChairId, (id) => {
  if (id === null || helpOpenedFromHash) return;
  helpOpenedFromHash = true;
  const hash = window.location.hash;
  if (hash.startsWith('#help:')) openHelp(hash.slice('#help:'.length));
}, { immediate: true });
</script>

<template>
  <WMOverlay />
  <FlashContainer :visual="{closeBtnText:''}" />

  <div v-if="!resolved" class="d-flex justify-content-center mt-5">
    <Loader />
  </div>
  <LoginForm v-else-if="!currentUser" @logged-in="resolve" />
  <ChairGate v-else-if="currentChairId === null" />
  <template v-else>
    <nav class="navbar navbar-expand-lg navbar-dark bg-dark">
      <div class="container-fluid">
        <router-link class="navbar-brand" :to="{name: 'home'}">Flopi</router-link>
        <div class="navbar-nav flex-row gap-3">
          <router-link class="nav-link" :to="{name: 'catalog'}">Procházet katalog</router-link>
          <router-link class="nav-link" :to="{name: 'running-proc-list'}">Spuštěné procesy</router-link>
          <router-link class="nav-link" :to="{name: 'admin-catalogs'}">Katalogy</router-link>
          <router-link class="nav-link" :to="{name: 'admin-requirements'}">Požadavky</router-link>
          <router-link class="nav-link" :to="{name: 'organization'}">Organizační struktura</router-link>
        </div>
        <div class="navbar-nav flex-row gap-3 ms-auto">
          <button type="button" class="nav-link btn btn-link" @click="openChairSwitcher">
            {{ currentUser.name }}<template v-if="currentChairLabel"> — {{ currentChairLabel }}</template>
          </button>
          <a href="#" class="nav-link" @click.prevent="openHelp()"><Icon icon="bookHelp" :title="t('help.open')" /></a>
          <a href="#" class="nav-link" @click.prevent="logout"><Icon icon="logout" :title="t('auth.logout')" /></a>
        </div>
      </div>
    </nav>
    <div class="container-fluid">
      <router-view />
    </div>
  </template>
</template>
